/**
 * Learn more drawer router.
 *
 * "Learn more" buttons carry `data-hotspot-handle`; on click we open the matching
 * `dialog-component.learn-more-drawer[data-linked-hotspot]` stacked over the products drawer.
 *
 * DialogComponent sees the existing scroll-lock and treats this as nested, so the drawer
 * behind stays open. `data-stacked` suppresses this drawer's backdrop to avoid double-darkening.
 *
 * No custom element here — the block reuses the global `dialog-component` (assets/dialog.js).
 */

const LEARN_MORE_BUTTON_SELECTOR = '.shop-the-look-products-drawer__learn-more-button';
const DRAWER_SELECTOR = 'dialog-component.learn-more-drawer';

/**
 * @param {string} handle
 * @returns {Element | null}
 */
function findDrawer(handle) {
  if (!handle) return null;
  const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(handle) : handle;
  return document.querySelector(`${DRAWER_SELECTOR}[data-linked-hotspot="${escaped}"]`);
}

/**
 * @param {Element | null} drawer
 */
function openDrawer(drawer) {
  const typed = /** @type {{ showDialog?: () => void } | null} */ (drawer);
  if (!typed || typeof typed.showDialog !== 'function') return;
  // Suppress the stacked drawer's own backdrop — the products drawer behind it already
  // dims the page (mirrors mini-pdp-drawer's data-stacked behavior).
  drawer?.querySelector('dialog')?.setAttribute('data-stacked', '');
  typed.showDialog();
}

/**
 * Reveals the "Learn more" buttons that actually have a drawer to open.
 * @param {Element} scope
 */
function revealButtonsWithDrawer(scope) {
  for (const button of scope.querySelectorAll(`${LEARN_MORE_BUTTON_SELECTOR}[hidden]`)) {
    if (button instanceof HTMLElement && findDrawer(button.dataset.hotspotHandle ?? '')) {
      button.hidden = false;
    }
  }
}

/**
 * @param {Event} event
 */
function handleClick(event) {
  const target = event.target instanceof Element ? event.target : null;
  const button = target?.closest(LEARN_MORE_BUTTON_SELECTOR);
  if (!(button instanceof HTMLElement)) return;

  const handle = button.dataset.hotspotHandle;
  if (!handle) return;

  const drawer = findDrawer(handle);
  if (!drawer) return;

  openDrawer(drawer);
}

// Guard against double-binding if the module is re-evaluated (e.g. Section Rendering API).
if (!window.__learnMoreDrawerWired) {
  window.__learnMoreDrawerWired = true;
  document.addEventListener('click', handleClick);
  revealButtonsWithDrawer(document);
  document.addEventListener(
    'dialog:open',
    (event) => {
      if (event.target instanceof Element) revealButtonsWithDrawer(event.target);
    },
    { capture: true },
  );

  // Theme editor: open/close the drawer when its block (or an ancestor block/section) is
  // selected so merchants can preview and configure it (the drawer has no on-page trigger
  // of its own).
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:block:select', (event) => {
      const scope = event.target instanceof Element ? event.target : null;
      openDrawer(scope?.querySelector(DRAWER_SELECTOR) ?? null);
    });
    document.addEventListener('shopify:block:deselect', (event) => {
      const scope = event.target instanceof Element ? event.target : null;
      const drawer = /** @type {{ closeDialog?: () => void } | null} */ (
        scope?.querySelector(DRAWER_SELECTOR) ?? null
      );
      if (drawer && typeof drawer.closeDialog === 'function') drawer.closeDialog();
    });
  }
}
